import { Box, VStack, HStack, Grid, Center, Icon, Text } from "@chakra-ui/react";
import { IoCalendarClearOutline, IoArrowBackOutline, IoArrowForwardOutline } from "react-icons/io5";
import { useState, useEffect, useRef } from "react";
import weekOfYear from 'dayjs/plugin/weekOfYear';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { getMonths } from '../../helpers';

dayjs.extend(weekOfYear);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault("Africa/Windhoek");

const WeeklyPicker = ({ styling, value, maxDate, onChange }) => {
    const daysOfTheWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const currentDate = dayjs();

    const [uiCalendarDate, setUICalendarDate] = useState(currentDate);
    const [activeMonth, setActiveMonth] = useState({});
    const [activeDate, setActiveDate] = useState(
        value.length
            ?   dayjs.unix(value[0])
            :   currentDate
    );

    const [showCalendar, setShowCalendar] = useState(0);
    const calendarRef = useRef(null);

    useEffect(() => {
        updateValue(value);
        const handleClickOutside = (event) => 
            calendarRef.current && !calendarRef.current.contains(event.target) && setShowCalendar(false);

        document.addEventListener("mousedown", handleClickOutside);
        return () => { document.removeEventListener("mousedown", handleClickOutside) };
    }, []);

    useEffect(() => {
        setUICalendarDate(activeDate);
        setActiveMonth(getMonths(activeDate));
    }, [activeDate]);

    useEffect(() => {
        updateValue(value);
    }, [value]);

    const updateValue = (value) => {
        value = value == null || !(value != null && value.length && value[0].toString().length )
            ?  dayjs().startOf("week").unix()
            : value[0];
        setActiveDate(dayjs.unix(value));
    }

    const getNextPrevMonth = (a) => {
        const newDate = a === "add" 
            ?   uiCalendarDate.add(1, 'month').startOf('month')
            :   uiCalendarDate.subtract(1, 'month').startOf('month');

        // check maxDate to limit selecting future dates
        const next =  {
            status: !(a === "add" && newDate.isAfter(dayjs.unix(maxDate).endOf('month'))),
            newDate
        };

        return next;
    }

    const handleMonthChange = (a) => {
        const next = getNextPrevMonth(a);
        if (!next.status) return;
        setUICalendarDate(next.newDate);
        setActiveMonth(getMonths(next.newDate));
    }

    const handleDateChange = (date) => {
        const selectedDate = dayjs(date);
        const startUnix = selectedDate.startOf('week').unix();
        const endUnix = selectedDate.endOf('week').unix();

        setActiveDate(selectedDate);
        onChange([startUnix, endUnix]);
    }

    // calendar styling
    const calendarStyling = styling?.calendar ? {
        [styling.calendar.placement[0]]: styling.calendar.spacing[0],
        [styling.calendar.placement[1]]: styling.calendar.spacing[1]
    } : {};
    
    return (
        <Box
            className="averly-date-picker week-picker"
            w={styling?.width || "auto"}
            h={styling?.height || "auto"}
            cursor="pointer"
            zIndex="999999"
            position="relative"
        >
            <VStack gap={styling?.spacing || "0"} position="relative">
                <HStack gap="5px" onClick={() => setShowCalendar(!showCalendar)}>
                    <Icon as={IoCalendarClearOutline} boxSize="16px" color="#171C1E" />
                    <Text fontSize={styling?.fontSize || "14px"} h={styling?.height || "auto"} display="inline-block" overflow="hidden">{ `${activeDate.startOf('week').format("DD MMM YYYY")} - ${activeDate.endOf('week').format("DD MMM YYYY")}`  }</Text>
                </HStack>
                <Box
                    ref={calendarRef}
                    bg="#F6F6F9"
                    w="356px"
                    h="auto"
                    position="absolute"
                    p="9px 5px 5px"
                    borderRadius="5px"
                    display={showCalendar ? "block" : "none"}
                    {...calendarStyling}
                    onMouseDown={(e) => e.preventDefault()}
                >
                    <VStack gap="2px" p="0">
                        <HStack className="header" w="100%" justifyContent="space-between">
                            <Center className="iconBackground" w="30px" h="30px" onClick={() => handleMonthChange("subtract")}>
                                <Icon as={IoArrowBackOutline} className="icon" boxSize="16px" />
                            </Center>
                            <Text fontSize="14px" color="#000" textAlign="center">{ uiCalendarDate.format("MMMM YYYY") }</Text>
                            <Center
                                className="iconBackground"
                                w="30px"
                                h="30px"
                                opacity={ !(getNextPrevMonth("add").newDate.isAfter(dayjs.unix(maxDate).endOf('month'))) ? 1 : 0}
                                onClick={() => handleMonthChange("add")}
                            >
                                <Icon as={IoArrowForwardOutline} className="icon" boxSize="16px" />
                            </Center>
                        </HStack>
                        <Box bg="#fff" w="100%" p="15px 15px 10px" borderRadius="3px" mt="10px">
                            <Grid gridTemplateColumns="repeat(7, 1fr)" gap="0" width="100%" mb="7px">
                                {
                                    daysOfTheWeek.map((a, i) => <Center w="100%" h="17px" fontSize="14px" color="#697387" key={i}>{a}</Center> )
                                }
                            </Grid>
                            {
                                activeMonth.weeks?.map((a, i) => {

                                    const activeWeekStatus = value != null && value.length &&  value[0].toString().length && ( dayjs.unix(value[0]).format("YYYY-MM-DD") === a[0] ) ? 1 : 0;
                                    const maxDateStatus = maxDate && dayjs(a[0]).format("YYYY-MM-DD") > dayjs.unix(maxDate).format("YYYY-MM-DD") ? 0 : 1;

                                    return <Box
                                        key={i}
                                        width="100%"
                                        px="2px"
                                        py="0"
                                        mb="5px"
                                        bg={activeWeekStatus ? "#344054" : "transparent"}
                                        borderRadius="17px"
                                        _hover={ activeWeekStatus ? { bg: "#344054" } : { bg: "#EAECF0" }}
                                        cursor={ maxDateStatus ? "pointer" : "not-allowed" }
                                        onClick={() => {
                                            if (!maxDateStatus) return;
                                            handleDateChange(a[0]);
                                            setTimeout(() => {
                                                setShowCalendar(0)
                                            }, 200);
                                        }}
                                    >
                                        <Grid gridTemplateColumns="repeat(7, 1fr)" gap="0" width="100%">
                                            {
                                                a.map((b, j) => {
                                                    const currentDayStatus = b === currentDate.format("YYYY-MM-DD") ? 1 : 0;
                                                    const currentMonthStatus = uiCalendarDate.format("MM") === dayjs(b).format("MM") ? 1 : 0;
                                                    return <Box w="100%" h="30px" display="flex" justifyContent="center" alignItems="center" key={j}>
                                                        <Center
                                                            w="30px"
                                                            h="30px"
                                                            bg={ currentDayStatus ? "#C3F0C2" : "transparent" }
                                                            border={ activeWeekStatus && currentDayStatus ? "2px solid #344054" : "none" }
                                                            borderRadius="50%"
                                                            fontSize="12px"
                                                            color={ currentDayStatus
                                                                ?   "#344054"
                                                                :   currentMonthStatus
                                                                    ?   activeWeekStatus
                                                                            ? "#fff"
                                                                            : "#344054"
                                                                    :   "#D0D5DD"
                                                            }
                                                        >
                                                            {dayjs(b).format("D")}
                                                        </Center>
                                                    </Box>
                                                })
                                            }
                                        </Grid>
                                    </Box>
                                })
                            }
                        </Box>
                    </VStack>
                </Box>
            </VStack>
        </Box>
    );
}

export default WeeklyPicker;